/**
 * Token utility functions for the auth feature.
 */

/**
 * Decodes a JWT token payload (without verification).
 * @param {string} token
 * @returns {object|null}
 */
export function decodeToken(token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
}

/**
 * Typed view of COP-32 access token payload (unsigned decode only).
 * @param {string|null|undefined} token
 * @returns {{ permissions?: number, roles?: string[], subject?: string, tokenVersion?: number } | null}
 */
export function getAccessTokenClaims(token) {
    const decoded = decodeToken(token);
    if (!decoded) return null;
    const roles = Array.isArray(decoded.roles)
        ? decoded.roles.filter((r) => typeof r === 'string')
        : undefined;
    return {
        ...(typeof decoded.permissions === 'number' && Number.isFinite(decoded.permissions)
            ? { permissions: decoded.permissions }
            : {}),
        ...(roles?.length ? { roles } : {}),
        ...(decoded.subject != null ? { subject: decoded.subject } : {}),
        ...(decoded.tokenVersion != null ? { tokenVersion: decoded.tokenVersion } : {}),
    };
}

/**
 * Checks if a JWT token is expired.
 * @param {string} token
 * @returns {boolean}
 */
export function isTokenExpired(token) {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    return Date.now() >= decoded.exp * 1000;
}

/**
 * Gets the remaining time in seconds until token expiration.
 * @param {string} token
 * @returns {number}
 */
export function getTokenRemainingTime(token) {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return 0;
    return Math.max(0, (decoded.exp * 1000 - Date.now()) / 1000);
}
